import { useEffect, useMemo, useState, type ReactElement } from "react";
import { Navigate } from "react-router-dom";
import { usePermissions } from "react-admin";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";

import { getApiUrl, TENANT_BRANDING_NAME_STORAGE_KEY } from "@/constants";
import httpClient from "@/dataprovider/http-client";
import {
  clearStoredTenantBranding,
  fetchAndStoreTenantBrandingTheme,
} from "@/utils/tenant-branding-cache";
import { hasPermission } from "@/utils/permission-utils";
import { AuthBrandedShell } from "@/layout/AuthBrandedShell";
import type { PermissionRecord } from "@/types/permissions";

type TenantOption = {
  id: string;
  name: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseTenantRows(payload: unknown): TenantOption[] {
  if (!isRecord(payload) || !Array.isArray(payload.rows)) {
    return [];
  }
  return payload.rows
    .filter(
      (row): row is { id: string; name: string } =>
        isRecord(row) && typeof row.id === "string" && typeof row.name === "string"
    )
    .map((row) => ({ id: row.id, name: row.name }));
}

export default function TenantSelectorPage(): ReactElement {
  const { permissions, isPending: permissionsPending } = usePermissions<PermissionRecord>();
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedTenantId, setSelectedTenantId] = useState("");

  const isSuperAdminByPermissions = hasPermission(permissions, "superAdmin.impersonate");
  const isSuperAdminBySession = localStorage.getItem("isSuperAdmin") === "1";
  const isSuperAdmin = isSuperAdminByPermissions || isSuperAdminBySession;

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    let ignore = false;
    const load = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const { json } = await httpClient(`${getApiUrl()}/super-admin/tenants`);
        const rows = parseTenantRows(json);
        if (ignore) return;
        setTenants(rows);
        if (rows.length > 0) {
          setSelectedTenantId(rows[0].id);
        }
      } catch (error: unknown) {
        if (ignore) return;
        setErrorMessage(error instanceof Error ? error.message : "Failed to load tenants");
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      ignore = true;
    };
  }, [isSuperAdmin]);

  const selectedTenant = useMemo(
    () => tenants.find((tenant) => tenant.id === selectedTenantId),
    [selectedTenantId, tenants]
  );

  const handleContinue = async (): Promise<void> => {
    if (!selectedTenantId) {
      setErrorMessage("Please select a tenant to continue.");
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      localStorage.setItem("impersonatedTenantId", selectedTenantId);
      clearStoredTenantBranding();
      if (selectedTenant?.name) {
        sessionStorage.setItem(TENANT_BRANDING_NAME_STORAGE_KEY, selectedTenant.name);
      }
      sessionStorage.setItem("permissionsCacheBust", String(Date.now()));
      await fetchAndStoreTenantBrandingTheme();
      // Use a query-parameter change (?_=<ts>) to force a full document reload.
      // A hash-only change (e.g. #/select-tenant → #/) does NOT reload the page,
      // so React Query's in-memory permission cache would survive and serve the
      // stale { superAdmin: { impersonate: true } } value on the dashboard.
      window.location.replace(`${window.location.origin}/?_=${Date.now()}#/`);
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to start tenant impersonation."
      );
    } finally {
      setSaving(false);
    }
  };

  // Redirect non-super-admins once permissions have loaded
  if (!permissionsPending && !isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <AuthBrandedShell logoMarginBottom="24px">
      {permissionsPending || loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2} sx={{ width: "100%" }}>
          <Typography variant="h6" align="center">
            Select Tenant
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            You are signed in as a super admin. Choose a tenant to continue.
          </Typography>
          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          <FormControl fullWidth disabled={saving}>
            <InputLabel id="tenant-selector-label">Tenant</InputLabel>
            <Select
              labelId="tenant-selector-label"
              label="Tenant"
              size="small"
              value={selectedTenantId}
              onChange={(event) => setSelectedTenantId(event.target.value)}
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.id} value={tenant.id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            onClick={handleContinue}
            disabled={saving || !selectedTenantId}
            sx={{ alignSelf: "flex-end" }}
            endIcon={saving ? <CircularProgress color="inherit" size={16} /> : null}
          >
            Continue
          </Button>
        </Stack>
      )}
    </AuthBrandedShell>
  );
}
